<template>
  <!--
    Spec 6.7, screen 3: what an approved device shows when another one is
    waiting — "on next launch when a PENDING device exists".

    ON ANY ROUTE, since #1691. This used to be gated on the part chooser and the
    Notes Hub, on the reading that a device waiting for approval is waiting on a
    member who is using the feature. That reading was wrong about where the
    member is standing: they are holding the new phone, which shows them an
    approval screen saying "Open HOGC One on a device you already use and tap
    Approve" — and this old phone, the only thing that can answer, would say
    nothing until they thought to open Next Steps on it. Screen 7's reset notice
    was moved into the app shell for the same reason, and until push ships (the
    mobile-half push issue under #1690) this is the free half of it.

    TWO THINGS STILL SILENCE IT. The reset notice goes first when both are due
    (`resetNoticeOnScreen`; ResetPendingNoticeHost.vue says why), and the two
    screens in `REQUEST_DEFERRING_PATHS` are left alone.

    MOUNTED ONCE, BY App.vue, AND GATED ON THE ROUTE — not mounted by each
    launch page. It used to be, and both pages are alive together in the Ionic
    stack the moment a member goes from the Hub to the chooser, so one request
    got two modals: COURSES-E2EE-002 found two "New device request" headings on
    screen, and the Start over form underneath two backdrops could not be
    reached at all (#1550). One host, one modal; the route says when.

    A MODAL, because `NewDeviceRequest.vue` is an `ion-page`: an ion-modal is
    the one container an ion-page belongs in, and this has to sit over whatever
    the member was doing rather than beneath it. Teleported to `ion-app` and
    presented programmatically, per docs/ION_MODAL_PATTERN.md — `:is-open`
    silently does nothing when it flips after mount, which is every case here.
    `defer` is load-bearing: this renders as a child of App.vue's own
    `<ion-app>`, which does not exist yet when its children mount.

    The teleport is why the mixin AND the v-bind are both needed: content
    teleported out of the page root leaves the page's replay-privacy stamp
    behind, so this binds its own. The device NAME a member gave their phone is
    the personal data in question.
  -->
  <Teleport defer to="ion-app">
    <ion-modal
      ref="ionModal"
      v-bind="privateRegionAttrs"
      :can-dismiss="true"
      @ionModalDidDismiss="onDismiss"
    >
      <NewDeviceRequest
        :device="device"
        :busy="busy"
        :error="error"
        :check="check"
        :scan-available="scanAvailable"
        @approve="onApprove"
        @close="onClose"
        @deny="onDeny"
        @retry="onRetry"
        @scan="onScan"
        @submit-code="onSubmitCode"
        @older-version="onOlderVersion"
        @reset="resetCheck"
      />
    </ion-modal>
  </Teleport>

  <!--
    ⚠️ THE SCAN SURFACE, TELEPORTED TO <body> AND NOT INTO THE MODAL.

    The camera preview renders BEHIND the WebView, so the member sees it only
    through an app that has got out of the way — and the rest of the app does
    that by drawing a viewfinder over a transparent page (`scanner/index.vue`,
    `photodrop`, `receive-photos`). This host could not: its scan runs from
    inside an <ion-modal>, an overlay layer with its own background, backdrop
    and shadow parts, and making every one of those transparent by CSS was
    tried (#1693) and still left the member looking at an opaque sheet.

    So the modal is taken off the screen for the length of the scan rather than
    argued with, and the viewfinder is teleported to <body>, outside the modal's
    stacking context — which is what the scanning pages have all along, being
    pages. `body` and not `ion-app`: an <ion-app> made transparent is still an
    ancestor whose stacking context a child cannot escape.

    The frame is the same one `scanner/index.vue` draws — a 9999px spread shadow
    darkening everything but the cutout — and the cancel is the same
    affordance: the plugin paints no UI of its own, so without one the member
    has a live camera and no way out but the app switcher.
  -->
  <Teleport to="body">
    <div v-if="scanning" class="fixed inset-0 z-[100000]">
      <div
        class="pointer-events-none absolute left-1/2 top-1/2 h-[80vw] max-h-[320px] w-[80vw] max-w-[320px] -translate-x-1/2 -translate-y-1/2 rounded-[24px] border-[3px] border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.6)]"
      ></div>
      <p
        class="pointer-events-none absolute left-1/2 top-[calc(50%+min(40vw,160px)+28px)] w-full -translate-x-1/2 px-6 text-center text-[0.9375rem] font-bold text-white"
      >
        {{ SCAN_COPY.aim }}
      </p>
      <button
        type="button"
        data-testid="approval-scan-cancel"
        class="fixed left-4 top-[calc(env(safe-area-inset-top,0px)+16px)] z-[100001] flex h-12 w-12 items-center justify-center rounded-full border-0 bg-grey-100/60 p-0 text-white shadow-elevation-1"
        :aria-label="SCAN_COPY.cancel"
        @click="onCancelScan"
      >
        <ion-icon :icon="closeOutline" class="text-[1.75rem]" aria-hidden="true"></ion-icon>
      </button>
    </div>
  </Teleport>
</template>

<script>
import { IonIcon, IonModal } from '@ionic/vue';
import { closeOutline } from 'ionicons/icons';
import { mapGetters } from 'vuex';
import privateRegion from '@/mixins/PrivateRegion';
import NewDeviceRequest from '@/pages/courses/encryption/NewDeviceRequest.vue';
import { REQUEST_DEFERRING_PATHS } from '@/constants/courses-encryption';
import {
  APPROVAL_CHECK,
  scanApprovalCode,
  // Aliased: the data field below is called `scanAvailable` too, and a helper
  // that reads like the field it fills is the kind of line a reviewer skips.
  scanAvailable as resolveScanAvailable,
  verifyTypedCode,
} from '@/utils/courses-encryption/approval-scan';
import {
  approvePendingDevice,
  denyPendingDevice,
} from '@/utils/courses-encryption/device-approval';
import { SCAN_COPY } from '@/pages/courses/encryption/approval-copy';
import { stopScan } from '@/utils/qr-scanner';

/**
 * On <body> while THIS host's camera is open. `theme/scanner-transparency.css`
 * takes the whole of <ion-app> off the screen under it — the scan surface is
 * teleported to <body>, outside ion-app, so it is the one thing left showing.
 * Own class rather than the plugin's `scanner-active`: the other scanning
 * pages draw their viewfinders INSIDE ion-app and would vanish with it.
 */
export const APPROVAL_SCAN_CLASS = 'approval-scan-active';
/** How often the approving phone asks whether a device is waiting, while on screen. */
export const REQUEST_POLL_INTERVAL_MS = 8000;

/**
 * The check states an approve may leave from — the same three the screen
 * enables its button on (`NewDeviceRequest.vue`), held here as well because
 * this is where the secret is chosen and sent. OLDER_VERSION is the explicit,
 * confirmed no-secret path; nothing else without a secret gets through.
 */
const APPROVABLE_CHECK_STATES = Object.freeze([
  APPROVAL_CHECK.MATCHED,
  APPROVAL_CHECK.TYPED,
  APPROVAL_CHECK.OLDER_VERSION,
]);

export default {
  name: 'PendingDeviceRequestHost',
  components: { IonIcon, IonModal, NewDeviceRequest },
  mixins: [privateRegion],
  data() {
    return {
      /**
       * The request the member said "Not now" to, as `id:createdAt` — so THAT
       * request stays quiet until the member asks for it from Devices, and a
       * different request (a new device, or the same device asking again under
       * a new identity) is shown at once. Until this existed, "Not now" was
       * for ever: nothing presented the modal again short of a relaunch
       * (DEV-15, 2026-09-11).
       */
      dismissedKey: null,
      /** The request Devices asked to review, shown ahead of the oldest. */
      preferredDeviceId: null,
      /** The foreground status poll; see `mounted`. */
      requestPollTimer: null,
      /**
       * True while a wrapping is being made, signed and sent. The screen
       * disables both buttons on it: approving is a challenge, a signature and
       * an ECDH wrap, several hundred milliseconds in which a second tap would
       * spend a second nonce and fail the first call.
       */
      busy: false,
      /** The last action, so Try again repeats what failed and not the other one. */
      lastAction: null,
      /**
       * What the out-of-band check has decided (#1693). Reactive, because the
       * screen renders it; only a STATE, never the secret behind it.
       */
      check: { state: APPROVAL_CHECK.IDLE },
      // Frozen module copy, held on the instance only so the template can name
      // it; nothing writes to it.
      SCAN_COPY,
      closeOutline,
      /** Whether this build can open a camera. Resolved once, in `mounted`. */
      scanAvailable: false,
      /**
       * Which scan `onScan` is waiting on. The plugin's `startScan` never
       * resolves once `stopScan` has been called — the other scanning pages
       * reset their own flag for that reason — so a cancelled scan leaves a
       * continuation that may fire much later, or never. Bumped on every start
       * and every cancel, and a continuation that comes back under an old
       * number changes nothing.
       */
      scanGeneration: 0,
    };
  },
  /**
   * ⚠️ THE SECRET LIVES HERE, OUTSIDE `data()`, AND THAT IS THE POINT.
   *
   * A property assigned in `created` is never made reactive, so it is not on
   * `$data`, does not appear in Vue devtools, and is not walked by a replay
   * serialiser or by anything else that enumerates a component's state. S is
   * not the master key, but it is the one thing standing between a substituted
   * public key and the member's notes, and it has no business in a reactive
   * tree that other tools read.
   *
   * `{ secret, publicKey }` when a scan matched, `{ secret }` when a code was
   * typed (there was no key to verify), null otherwise.
   */
  created() {
    this.verified = null;
  },
  computed: {
    ...mapGetters('courses-devices', ['pendingDevices', 'resetNoticeOnScreen']),
    ...mapGetters('courses-keys', ['hasMasterKey']),
    /**
     * One request at a time. Two devices waiting is rare, and nothing
     * serialises two signed operations — the API keeps ONE live nonce per
     * member, so a second approve overlapping the first makes the loser fail as
     * a non-retryable `signature-rejected`. Answering them one after another is
     * what keeps that from happening.
     */
    device() {
      const preferred = this.preferredDeviceId
        ? this.pendingDevices.find(device => device.id === this.preferredDeviceId)
        : null;
      return preferred ?? this.pendingDevices[0] ?? null;
    },
    /**
     * The camera is live, so the app is off the screen and the viewfinder is on
     * it. Driven by the check state rather than a second flag: `onScan` already
     * owns exactly this window, and two sources for one truth is how a cancel
     * ends up leaving the modal hidden with no camera behind it.
     */
    scanning() {
      return this.check.state === APPROVAL_CHECK.SCANNING;
    },
    /**
     * On one of the two screens that are already about this, where a modal
     * would cover the control the member came for (`REQUEST_DEFERRING_PATHS`).
     *
     * Read from the matched route RECORD, not `$route.path`: the router reports
     * the path as typed, so a string compare would let a deep link with a
     * trailing slash present a modal over the Start over form — silently, and
     * only for the member who got there by a link.
     */
    onDeferringPage() {
      return (this.$route?.matched ?? []).some(record =>
        REQUEST_DEFERRING_PATHS.includes(record.path),
      );
    },
    /**
     * Only a device that holds MK can wrap it to another, so only that device
     * is ever asked. `pendingDevices` already excludes this install's own row.
     *
     * The two silences are last because they are exceptions to a rule that is
     * otherwise "wherever the member is": a screen already about this, and the
     * reset notice, which is destructive and goes first (#1691).
     */
    visible() {
      if (!this.hasMasterKey || !this.device) return false;
      if (this.requestKeyOf(this.device) === this.dismissedKey) return false;
      return !this.onDeferringPage && !this.resetNoticeOnScreen;
    },
    /** The Devices screen asked for a request to be shown again. State, not a getter: some hosts mount before the module. */
    reviewRequestId() {
      return this.$store.state['courses-devices']?.reviewRequestId ?? null;
    },
    /** `courses-devices` records the member's last failure; the screen reads it. */
    error() {
      return this.$store.state['courses-devices'].error;
    },
  },
  watch: {
    /**
     * The app off the screen while the camera is on, and back the moment it is
     * not. Whatever ended the scan — a read, a cancel, the request being
     * withdrawn under it — the camera is put down here, in one place, rather
     * than by each of those paths remembering to. `stopScan` tolerates a camera
     * that is already closed.
     */
    scanning(active) {
      document.body?.classList.toggle(APPROVAL_SCAN_CLASS, active);
      if (!active) {
        stopScan().catch(error => console.warn('[CoursesE2EE] The scan would not stop:', error));
      }
    },
    /**
     * A verification belongs to ONE device and must never carry to the next.
     *
     * The list can change under this modal without anybody touching it: the
     * poll refreshes every four seconds, a Deny empties `pendingDevices`, and a
     * second phone can arrive behind the first. A `check` left at MATCHED
     * across that swap would leave Approve live for a device whose code nobody
     * has read — the exact hole the check exists to close — so the state goes
     * back to IDLE and the bytes are zeroed.
     */
    'device.id': {
      handler() {
        this.resetCheck();
      },
    },
    /**
     * A dismissal is immediate; a presentation waits a tick.
     *
     * WHY THE TICK. The reset notice's host commits `resetNoticeOnScreen` from
     * its OWN watcher, and watcher jobs in one flush run in component-creation
     * order. Presenting synchronously would make this correct only because
     * `ResetPendingNoticeHost` happens to sit above this one in App.vue's
     * template: reverse those two lines and this modal presents first and is
     * dismissed from underneath the notice a moment later — two stacked modals
     * and a teardown that cannot reach the screen beneath, which is #1550.
     *
     * Re-reading `visible` after the flush costs one tick on a modal that
     * animates for far longer, and makes the precedence a fact about the store
     * rather than about where two lines sit in a template.
     */
    visible(open) {
      if (!open) {
        this.toggle(false);
        return;
      }
      this.presentIfStillDue();
    },
    /**
     * "Review" on the Devices screen: forget the "Not now", put that request
     * first, and present. The flag is consumed here so a second Review works.
     */
    reviewRequestId(id) {
      if (!id) return;
      // Clearing the Not now flips `visible`, whose watcher presents; a modal
      // already on screen simply shows the preferred request instead.
      this.dismissedKey = null;
      this.preferredDeviceId = id;
      this.$store.commit('courses-devices/setReviewRequest', null);
    },
  },
  mounted() {
    /*
     * HOW THIS PHONE LEARNS THAT ANOTHER ONE IS WAITING. Until now: a status
     * read at launch, on resume, on entering Next Steps or the Me tab, or a
     * push tap — and nothing in between. The waiting device polls every four
     * seconds; the approving one did not poll at all, so the member walked in
     * and out of screens until a read happened to land (2026-09-11). One GET
     * every eight seconds, only on a device that could answer (it holds the
     * key) and only while the app is on screen.
     */
    this.requestPollTimer = setInterval(() => this.pollForRequests(), REQUEST_POLL_INTERVAL_MS);
    // Once, at boot; a status read that landed first would leave the modal shut.
    //
    // TWO TICKS, not one. The first is the deferred teleport — the modal element
    // does not exist before it (docs/ION_MODAL_PATTERN.md). The second is the
    // precedence: `ResetPendingNoticeHost` presents from its own `mounted` on
    // the FIRST tick, and if it is declared below this host in App.vue its
    // callback is registered second, so on tick one `resetNoticeOnScreen` is
    // still false and this would present under a notice about to appear.
    this.$nextTick(() => this.presentIfStillDue());
    // Once: the answer is a platform fact, not a per-request one, and the
    // screen needs it before the member can tap anything. It never rejects.
    resolveScanAvailable().then(available => {
      this.scanAvailable = available;
    });
  },
  beforeUnmount() {
    clearInterval(this.requestPollTimer);
    this.requestPollTimer = null;
    document.body?.classList.remove(APPROVAL_SCAN_CLASS);
  },
  methods: {
    /** One status read, on a device that could answer a request, while on screen. */
    pollForRequests() {
      if (!this.hasMasterKey) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      this.$store.dispatch('courses-devices/refreshStatus').catch(() => {});
    },
    /**
     * Present, but only if still due once the current flush has finished.
     *
     * The reset notice's host publishes `resetNoticeOnScreen` from its own mount
     * and watcher, and Vue runs both hosts' jobs in component-creation order —
     * template order. Reading `visible` a tick later makes the precedence a fact
     * about the store rather than about which of the two lines in App.vue comes
     * first, so neither declaration order can stack a request over a notice.
     */
    presentIfStillDue() {
      this.$nextTick(() => {
        if (this.visible) this.toggle(true);
      });
    },
    toggle(open) {
      const modal = this.$refs.ionModal?.$el;
      // The teleport is deferred, so the element can exist before it is an
      // ion-modal: nothing to present or dismiss yet, and the next `visible`
      // change presents it if it is still due.
      const method = open ? 'present' : 'dismiss';
      if (typeof modal?.[method] !== 'function') return;
      modal[method]();
    },
    /**
     * A member who swipes the modal away has answered neither way, so the
     * request stays PENDING on the server and comes back on the next launch.
     * Only the in-flight flag is cleared.
     */
    /** `id:createdAt`: the same device asking again under a new identity is a new request. */
    requestKeyOf(device) {
      return device ? `${device.id}:${device.createdAt ?? ''}` : null;
    },
    onDismiss() {
      // Dismissed while still due means the MEMBER closed it — Not now, or a
      // swipe. A dismissal because the request was answered, or because the
      // route changed, finds `visible` false and records nothing.
      if (this.visible) this.dismissedKey = this.requestKeyOf(this.device);
      this.busy = false;
      // The next launch asks again, and it must ask from the beginning: a
      // verification that outlived the modal it was made in is a verification
      // nobody can vouch for.
      this.resetCheck();
    },
    /**
     * Back to "nothing read", with the bytes zeroed rather than dropped.
     *
     * Zeroing matters more here than the assignment does: `verified.secret` is
     * a Uint8Array, and setting the reference to null leaves the buffer sitting
     * in the heap until a collection nobody controls. `fill(0)` is what
     * actually removes it.
     */
    resetCheck() {
      if (this.verified?.secret?.fill) this.verified.secret.fill(0);
      this.verified = null;
      this.check = { state: APPROVAL_CHECK.IDLE };
    },
    /**
     * Open the camera and take whatever `approval-scan.js` decides. It never
     * rejects, so there is nothing to catch: a refusal, a wrong phone, junk, a
     * cancelled scan and a missing camera all arrive as states, and only
     * MATCHED leaves anything behind.
     */
    /**
     * Put the camera down. The plugin paints no UI of its own, so this button is
     * the only way out of a live scan that is not the app switcher — the same
     * affordance `scanner/index.vue` gives (`cancelScan`).
     *
     * ⚠️ `stopScan` does NOT make the pending `startScan` resolve — it hangs,
     * which is why every scanning page in this app resets its own flag on
     * cancel rather than waiting for the plugin. The first version of this
     * waited, and the member who cancelled kept the viewfinder for ever, over
     * a modal that had come back underneath it.
     */
    onCancelScan() {
      // The state, not the camera: `scanning` going false is what puts the
      // camera down (see the watcher), and moving the generation on is what
      // makes the scan that was running unable to report back.
      this.scanGeneration += 1;
      this.check = { state: APPROVAL_CHECK.CANCELLED };
    },
    async onScan() {
      this.resetCheck();
      this.scanGeneration += 1;
      const generation = this.scanGeneration;
      this.check = { state: APPROVAL_CHECK.SCANNING };
      const { state, secret, publicKey } = await scanApprovalCode({ device: this.device });
      // Cancelled, or a later scan has started: this answer belongs to a camera
      // the member already put down.
      if (generation !== this.scanGeneration) return;
      // The VERIFIED key, not the row's copy of it: what the approve wraps to
      // is the key the member's own camera read.
      if (state === APPROVAL_CHECK.MATCHED) this.verified = { secret, publicKey };
      this.check = { state };
    },
    /**
     * The typed carrier. No public key was read, so none is verified and the
     * wrap goes to the row's key — with S folded into the wrapping key, which
     * is what makes a substituted key useless (`approval-scan.js` says why).
     *
     * @param {string} text exactly what the member typed
     */
    onSubmitCode(text) {
      this.resetCheck();
      const { state, secret } = verifyTypedCode(text);
      if (state === APPROVAL_CHECK.TYPED) this.verified = { secret };
      this.check = { state };
    },
    /**
     * The member says the other phone is on a build with no code to show. No
     * secret, so the wrapping is `v: 1` — which is why the screen puts this
     * behind a confirm and leaves a standing sentence for as long as Approve is
     * live.
     */
    onOlderVersion() {
      this.resetCheck();
      this.check = { state: APPROVAL_CHECK.OLDER_VERSION };
    },
    /**
     * The race decision 15 leaves open ends here on screen.
     *
     * `approvePendingDevice` refuses when the row's key no longer matches the
     * one the member verified, and it refuses BEFORE dispatching — so there is
     * no `courses-devices` state.error for the screen to render, and without
     * this the member would tap Approve and watch nothing happen. It is the
     * same event as a mismatched scan, arriving one poll later, so it gets the
     * same state and the same sentence: Approve goes dead, and the refusal
     * block says to deny.
     */
    /**
     * Leave the request unanswered. `dismiss()` rather than a flag: the modal
     * owns whether it is on screen, and `onDismiss` already puts the check back
     * to IDLE and zeroes any secret read. The request stays PENDING and the
     * poll offers it again — which is what makes this safe to do at any point,
     * including mid-scan.
     */
    onClose() {
      return this.$refs.ionModal?.$el?.dismiss?.();
    },
    onApprove() {
      /*
       * ⚠️ THE HOST DECIDES THE SECRET, SO THE HOST GATES THE APPROVE. The
       * screen disables its button until the check has passed, and that was
       * the only gate: an `approve` arriving in any other state — a failed
       * check, a cancelled scan, nothing read — went out as a `v: 1` wrapping
       * with no secret at all, which is the older-version fallback without the
       * confirm the older-version fallback sits behind. A button state is not
       * a security boundary; the seam that puts the secret on the wire is.
       */
      if (!APPROVABLE_CHECK_STATES.includes(this.check.state)) {
        console.warn(`[CoursesE2EE] Approve refused: the check is "${this.check.state}"`);
        return Promise.resolve();
      }
      return this.run(async () => {
        const result = await approvePendingDevice({
          store: this.$store,
          device: this.device,
          secret: this.verified?.secret,
          verifiedPublicKey: this.verified?.publicKey,
        });
        if (result?.reason === 'public-key-changed') {
          this.resetCheck();
          this.check = { state: APPROVAL_CHECK.REFUSED_KEY };
        }
        return result;
      });
    },
    onDeny() {
      return this.run(() => denyPendingDevice({ store: this.$store, device: this.device }));
    },
    onRetry() {
      if (this.lastAction) return this.run(this.lastAction);
      return undefined;
    },
    /**
     * One answer at a time. Both store actions refresh the status themselves,
     * so an approve or a deny that succeeded empties `pendingDevices` and the
     * modal closes on its own — and a failure leaves it open with the sentence
     * the screen renders from `state.error`.
     *
     * @param {() => Promise<{ outcome: string }>} action
     */
    async run(action) {
      if (this.busy) return;
      this.busy = true;
      this.lastAction = action;
      try {
        await action();
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
/*
  While this host's camera is open, `theme/scanner-transparency.css` takes the
  whole of <ion-app> off the screen (`APPROVAL_SCAN_CLASS`), and the scan
  surface above is teleported to <body>, outside it. Nothing here has to make
  the modal transparent any more — making an Ionic overlay transparent part by
  part was tried twice (#1693) and each time a part was missed, because that
  list is never demonstrably complete.

  The transparency rules the plugin relies on (`scanner-active`) are global,
  in that same file. This block is kept only so the host's own styling has a
  home; it is intentionally empty of scanner rules.
*/
</style>
